// Per-project detail store: project + phases + tasks indexed by phase.
// Each route mount calls useProjectDetail(id) which (re)loads the snapshot.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type {
  AttachmentMeta,
  LabelCreate,
  LabelItem,
  Phase,
  PhaseCreate,
  PhaseReorder,
  PhaseUpdate,
  ProjectGantt,
  ProjectWithCounts,
  Task,
  TaskCreate,
  TaskDependency,
  TaskReorder,
  TaskUpdate,
  TaskUpdateCreate,
  TaskUpdateItem,
  TaskNoteCreate,
  TaskNoteItem,
  TaskMergeResult,
} from "@juuplan/shared";
import { api } from "../lib/api";
import { toastError } from "../lib/toast";
import { optimistic } from "../lib/optimistic";

export interface ProjectDetailState {
  loading: boolean;
  project: ProjectWithCounts | null;
  tasksByPhase: Record<number, Task[]>;
  error: string | null;
}

const initial: ProjectDetailState = {
  loading: true,
  project: null,
  tasksByPhase: {},
  error: null,
};
export const projectSignal: Signal<ProjectDetailState> = signal(initial);
let loadedId: number | null = null;
let inFlight: Promise<void> | null = null;

function groupByPhase(tasks: Task[]): Record<number, Task[]> {
  const out: Record<number, Task[]> = {};
  for (const t of tasks) {
    const arr = out[t.phase_id] ?? (out[t.phase_id] = []);
    arr.push(t);
  }
  for (const list of Object.values(out)) {
    list.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  }
  return out;
}

// Monotonic request id — a stale response (fast A→B navigation) must never
// clobber the newer project's data or reset loadedId backwards.
let loadSeq = 0;

async function loadProject(id: number): Promise<void> {
  const seq = ++loadSeq;
  projectSignal.value = { ...projectSignal.value, loading: true, error: null };
  try {
    const [project, tasks] = await Promise.all([
      api<ProjectWithCounts>(`/api/projects/${id}`),
      api<Task[]>(`/api/projects/${id}/tasks`),
    ]);
    if (seq !== loadSeq) return; // superseded by a newer load
    projectSignal.value = {
      loading: false,
      project,
      tasksByPhase: groupByPhase(tasks),
      error: null,
    };
    loadedId = id;
  } catch (err) {
    if (seq !== loadSeq) return;
    const message = err instanceof Error ? err.message : "Unknown error";
    projectSignal.value = {
      loading: false,
      project: null,
      tasksByPhase: {},
      error: message,
    };
  } finally {
    if (seq === loadSeq) inFlight = null;
  }
}

export function useProjectDetail(id: number): ProjectDetailState {
  useEffect(() => {
    if (loadedId !== id || !projectSignal.value.project) {
      loadedId = id;
      inFlight = loadProject(id);
    }
  }, [id]);
  return projectSignal.value;
}

export function refreshProjectDetail(): Promise<void> {
  if (loadedId === null) return Promise.resolve();
  inFlight = loadProject(loadedId);
  return inFlight;
}

// ------------ project mutations ------------

export async function patchProject(
  id: number,
  patch: Partial<ProjectWithCounts>
): Promise<void> {
  await api(`/api/projects/${id}`, { method: "PATCH", body: patch });
  // Department-Updates beeinflussen die Sidebar-Liste → projects-Cache mit
  // auffrischen damit der Department-Filter direkt greift.
  await Promise.all([
    refreshProjectDetail(),
    (await import("./projects")).refreshProjects(),
  ]);
}

// ------------ phase mutations ------------

export async function createPhase(
  projectId: number,
  input: PhaseCreate
): Promise<Phase> {
  const phase = await api<Phase>(`/api/projects/${projectId}/phases`, {
    method: "POST",
    body: input,
  });
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
  return phase;
}

export async function patchPhase(id: number, patch: PhaseUpdate): Promise<void> {
  await api<Phase>(`/api/phases/${id}`, { method: "PATCH", body: patch });
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
}

export async function deletePhase(id: number): Promise<void> {
  await api(`/api/phases/${id}`, { method: "DELETE" });
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
}

export async function reorderPhase(id: number, body: PhaseReorder): Promise<void> {
  const state = projectSignal.value;
  const proj = state.project;
  const moving = proj?.phases.find((p) => p.id === id);

  // Optimistisch: die Phasen-Reihenfolge sofort lokal umsortieren (der Render
  // ordnet nach Array-Position), den Server-Call im Hintergrund. Kein teures
  // Voll-Reload mehr vor der Anzeige — fuehlt sich sofort an. Bei Fehler
  // refetchen wir und stellen damit die Server-Wahrheit wieder her.
  if (proj && moving) {
    const without = proj.phases.filter((p) => p.id !== id);
    const dest = Math.max(0, Math.min(body.sort_order, without.length));
    const newPhases = [
      ...without.slice(0, dest),
      moving,
      ...without.slice(dest),
    ];
    projectSignal.value = { ...state, project: { ...proj, phases: newPhases } };
    try {
      await api(`/api/phases/${id}/reorder`, { method: "PATCH", body });
      void refreshGantt(); // Hintergrund — blockiert die Anzeige nicht.
    } catch {
      await refreshProjectDetail(); // revert auf Server-Stand
    }
    return;
  }

  // Fallback ohne lokalen State: klassisch patchen + neu laden.
  await api(`/api/phases/${id}/reorder`, { method: "PATCH", body });
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
}

// ------------ task mutations ------------

async function refreshAll(): Promise<void> {
  // Keep both detail (TableView) and Gantt in sync — they share the same
  // underlying mutations.
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
}

export async function createTask(
  phaseId: number,
  input: TaskCreate
): Promise<Task> {
  const task = await api<Task>(`/api/phases/${phaseId}/tasks`, {
    method: "POST",
    body: input,
  });
  await refreshAll();
  return task;
}

export async function patchTask(id: number, patch: TaskUpdate): Promise<void> {
  await api<Task>(`/api/tasks/${id}`, { method: "PATCH", body: patch });
  await refreshAll();
}

/**
 * Optimistic task mutation for STATUS (status_id), PRIORITY, and ASSIGNEE
 * (assignee_ids) changes.
 *
 * Immediately applies `patch` fields onto the matching task in
 * `projectSignal.tasksByPhase`, fires the API PATCH, then reconciles with the
 * authoritative server response. On rejection the pre-mutation snapshot is
 * restored and an error toast is shown. The server response is always the
 * source of truth — §Aegis7.1.
 *
 * @param id    Task id to patch.
 * @param patch Fields to change. Only keys present in the patch are applied
 *              optimistically; all other task fields are left as-is.
 */
export async function patchTaskOptimistic(
  id: number,
  patch: Pick<TaskUpdate, "status_id" | "priority" | "assignee_ids">
): Promise<void> {
  const state = projectSignal.value;

  // Build the optimistic next state by patching the target task inside
  // tasksByPhase without touching any other tasks or fields.
  function applyPatch(
    tasksByPhase: ProjectDetailState["tasksByPhase"],
    changes: Partial<Task>
  ): ProjectDetailState["tasksByPhase"] {
    const next: Record<number, Task[]> = {};
    for (const [phaseKey, list] of Object.entries(tasksByPhase)) {
      next[Number(phaseKey)] = list.map((t) =>
        t.id === id ? { ...t, ...changes } : t
      );
    }
    return next;
  }

  // Translate patch fields into Task-shape changes for the optimistic write.
  const taskChanges: Partial<Task> = {};
  if ("status_id" in patch && patch.status_id !== undefined) {
    // status_id lives on the server model; we clear statusInfo so stale badges
    // don't linger — reconcile will set the authoritative value.
    (taskChanges as Record<string, unknown>)["status_id"] = patch.status_id;
    taskChanges.statusInfo = null;
  }
  if ("priority" in patch) {
    taskChanges.priority = patch.priority ?? null;
  }
  if ("assignee_ids" in patch && patch.assignee_ids !== undefined) {
    taskChanges.assignee_ids = patch.assignee_ids;
    taskChanges.primary_assignee_id = patch.assignee_ids[0] ?? null;
  }

  const nextState: ProjectDetailState = {
    ...state,
    tasksByPhase: applyPatch(state.tasksByPhase, taskChanges),
  };

  try {
    await optimistic({
      read: () => projectSignal.value,
      write: (v) => { projectSignal.value = v; },
      next: nextState,
      commit: async () => {
        // Server is authoritative: reconcile the returned task into the signal.
        const serverTask = await api<Task>(`/api/tasks/${id}`, {
          method: "PATCH",
          body: patch,
        });
        const current = projectSignal.value;
        projectSignal.value = {
          ...current,
          tasksByPhase: applyPatch(current.tasksByPhase, serverTask as Partial<Task>),
        };
      },
    });
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "Änderung konnte nicht gespeichert werden.";
    toastError(message);
  }
}

export async function deleteTask(id: number): Promise<void> {
  await api(`/api/tasks/${id}`, { method: "DELETE" });
  await refreshAll();
}

/**
 * Merge `sourceId` into `targetTaskId` (same project only — enforced server-side).
 * The source becomes a closed gravestone (`merged_into_task_id`); refreshAll()
 * afterwards pulls both the frozen source and the enriched target back in.
 */
export async function mergeTask(sourceId: number, targetTaskId: number): Promise<TaskMergeResult> {
  const result = await api<TaskMergeResult>(`/api/tasks/${sourceId}/merge`, {
    method: "POST",
    body: { target_task_id: targetTaskId },
  });
  await refreshAll();
  return result;
}

export async function reorderTask(id: number, body: TaskReorder): Promise<void> {
  await api(`/api/tasks/${id}/reorder`, { method: "PATCH", body });
  await refreshAll();
}

// ------------ task dependencies ------------

export async function fetchTaskDependencies(
  taskId: number
): Promise<TaskDependency[]> {
  return api<TaskDependency[]>(`/api/tasks/${taskId}/dependencies`);
}

export async function addTaskDependency(
  taskId: number,
  dependsOnId: number
): Promise<void> {
  await api(`/api/tasks/${taskId}/dependencies`, {
    method: "POST",
    body: { depends_on_id: dependsOnId },
  });
  // Projekt-Detail neu laden, damit das dependency_count-Icon in der Tabelle
  // sofort erscheint (nicht erst nach manuellem Reload). Gantt mit aktualisieren.
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
}

export async function removeTaskDependency(
  taskId: number,
  dependsOnId: number
): Promise<void> {
  await api(`/api/tasks/${taskId}/dependencies/${dependsOnId}`, {
    method: "DELETE",
  });
  await Promise.all([refreshProjectDetail(), refreshGantt()]);
}

// ------------ task updates (activity feed) ------------

export async function fetchTaskUpdates(taskId: number): Promise<TaskUpdateItem[]> {
  return api<TaskUpdateItem[]>(`/api/tasks/${taskId}/updates`);
}

export async function postTaskUpdate(
  taskId: number,
  payload: TaskUpdateCreate
): Promise<TaskUpdateItem> {
  return api<TaskUpdateItem>(`/api/tasks/${taskId}/updates`, {
    method: "POST",
    body: payload,
  });
}

export async function deleteTaskUpdate(
  taskId: number,
  updateId: number
): Promise<void> {
  await api(`/api/tasks/${taskId}/updates/${updateId}`, { method: "DELETE" });
}

// ------------ task notes / comments ------------
// Eigenständiges Notizen-Feld (getrennt vom Aktivitäts-Feed). Neueste zuerst.

export async function fetchTaskNotes(taskId: number): Promise<TaskNoteItem[]> {
  return api<TaskNoteItem[]>(`/api/tasks/${taskId}/notes`);
}

export async function createTaskNote(
  taskId: number,
  payload: TaskNoteCreate
): Promise<TaskNoteItem> {
  return api<TaskNoteItem>(`/api/tasks/${taskId}/notes`, {
    method: "POST",
    body: payload,
  });
}

export async function updateTaskNote(
  taskId: number,
  noteId: number,
  payload: TaskNoteCreate
): Promise<TaskNoteItem> {
  return api<TaskNoteItem>(`/api/tasks/${taskId}/notes/${noteId}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function deleteTaskNote(
  taskId: number,
  noteId: number
): Promise<void> {
  await api(`/api/tasks/${taskId}/notes/${noteId}`, { method: "DELETE" });
}

// Persönliche Aufgaben (Aufgabenlisten) — eigener, self-guarded Endpunkt.
export async function fetchPersonalTaskNotes(
  personalTaskId: number
): Promise<TaskNoteItem[]> {
  return api<TaskNoteItem[]>(`/api/personal-tasks/${personalTaskId}/notes`);
}

export async function createPersonalTaskNote(
  personalTaskId: number,
  payload: TaskNoteCreate
): Promise<TaskNoteItem> {
  return api<TaskNoteItem>(`/api/personal-tasks/${personalTaskId}/notes`, {
    method: "POST",
    body: payload,
  });
}

export async function updatePersonalTaskNote(
  personalTaskId: number,
  noteId: number,
  payload: TaskNoteCreate
): Promise<TaskNoteItem> {
  return api<TaskNoteItem>(
    `/api/personal-tasks/${personalTaskId}/notes/${noteId}`,
    { method: "PATCH", body: payload }
  );
}

export async function deletePersonalTaskNote(
  personalTaskId: number,
  noteId: number
): Promise<void> {
  await api(`/api/personal-tasks/${personalTaskId}/notes/${noteId}`, {
    method: "DELETE",
  });
}

// ------------ gantt data ------------
// Separate signal for the aggregated /gantt payload — kept apart from
// projectSignal so that views that don't need dependencies don't pay the cost.

export interface GanttState {
  loading: boolean;
  data: ProjectGantt | null;
  error: string | null;
  projectId: number | null;
}

const initialGantt: GanttState = {
  loading: true,
  data: null,
  error: null,
  projectId: null,
};
const ganttSignal: Signal<GanttState> = signal(initialGantt);
let ganttInFlight: Promise<void> | null = null;

async function loadGantt(projectId: number): Promise<void> {
  ganttSignal.value = { ...ganttSignal.value, loading: true, error: null };
  try {
    const data = await api<ProjectGantt>(`/api/projects/${projectId}/gantt`);
    ganttSignal.value = { loading: false, data, error: null, projectId };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    ganttSignal.value = {
      loading: false,
      data: null,
      error: message,
      projectId,
    };
  } finally {
    ganttInFlight = null;
  }
}

export function useGanttData(projectId: number): GanttState {
  useEffect(() => {
    if (ganttSignal.value.projectId !== projectId || !ganttSignal.value.data) {
      ganttInFlight = loadGantt(projectId);
    }
  }, [projectId]);
  return ganttSignal.value;
}

export function refreshGantt(): Promise<void> {
  const id = ganttSignal.value.projectId;
  if (id === null) return Promise.resolve();
  ganttInFlight = loadGantt(id);
  return ganttInFlight;
}

// ------------ attachments (Story 6) ------------

export async function fetchAttachments(taskId: number): Promise<AttachmentMeta[]> {
  return api<AttachmentMeta[]>(`/api/tasks/${taskId}/attachments`);
}

/**
 * Server expects JSON {filename, content_type, base64} (same shape as the
 * project/personal-task uploads below) — there's no @fastify/multipart in the
 * stack, so a FormData body here always 415'd with "Unsupported Media Type"
 * before it ever reached the route handler.
 */
export async function uploadAttachment(
  taskId: number,
  file: File
): Promise<AttachmentMeta> {
  const base64 = await fileToBase64(file);
  const meta = await api<AttachmentMeta>(`/api/tasks/${taskId}/attachments`, {
    method: "POST",
    body: {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      base64,
    },
  });
  // Bump the task's attachment_count in the project signal optimistically so the
  // paperclip badge updates without waiting for a full refresh.
  bumpAttachmentCount(taskId, +1);
  return meta;
}

export async function deleteAttachment(id: number, taskId?: number): Promise<void> {
  await api(`/api/attachments/${id}`, { method: "DELETE" });
  if (taskId !== undefined) bumpAttachmentCount(taskId, -1);
}

// ------------ personal-task attachments (Aufgabenlisten) ------------

export async function fetchPersonalTaskAttachments(
  personalTaskId: number
): Promise<AttachmentMeta[]> {
  return api<AttachmentMeta[]>(`/api/personal-tasks/${personalTaskId}/attachments`);
}

export async function uploadPersonalTaskAttachment(
  personalTaskId: number,
  file: File
): Promise<AttachmentMeta> {
  // JSON base64 (gleiche Shape wie Projekt-Upload) — kein Multipart im Stack.
  const base64 = await fileToBase64(file);
  return api<AttachmentMeta>(`/api/personal-tasks/${personalTaskId}/attachments`, {
    method: "POST",
    body: {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      base64,
    },
  });
}

// ------------ project-attachments (Sprint 3 Story 6 extension) ------------

export async function fetchProjectAttachments(
  projectId: number
): Promise<AttachmentMeta[]> {
  return api<AttachmentMeta[]>(`/api/projects/${projectId}/attachments`);
}

export async function uploadProjectAttachment(
  projectId: number,
  file: File
): Promise<AttachmentMeta> {
  // Server erwartet JSON {filename, content_type, base64} (gleiche Shape wie
  // /api/tasks/:id/attachments). Wir kodieren File via FileReader.
  const base64 = await fileToBase64(file);
  return api<AttachmentMeta>(`/api/projects/${projectId}/attachments`, {
    method: "POST",
    body: {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      base64,
    },
  });
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  // Browser-safe Base64 ohne Stack-Overflow auf großen Files.
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunk))
    );
  }
  return btoa(binary);
}

function bumpAttachmentCount(taskId: number, delta: number): void {
  const state = projectSignal.value;
  let changed = false;
  const next: Record<number, Task[]> = {};
  for (const [phaseId, list] of Object.entries(state.tasksByPhase)) {
    let listChanged = false;
    const nextList = list.map((t) => {
      if (t.id !== taskId) return t;
      listChanged = true;
      changed = true;
      return {
        ...t,
        attachment_count: Math.max(0, t.attachment_count + delta),
      };
    });
    next[Number(phaseId)] = listChanged ? nextList : list;
  }
  if (changed) {
    projectSignal.value = { ...state, tasksByPhase: next };
  }
}

// ------------ labels (multi-tag column) ------------

export async function fetchLabels(projectId: number): Promise<LabelItem[]> {
  return api<LabelItem[]>(`/api/projects/${projectId}/labels`);
}

export async function createLabel(
  projectId: number,
  payload: LabelCreate
): Promise<LabelItem> {
  return api<LabelItem>(`/api/projects/${projectId}/labels`, {
    method: "POST",
    body: payload,
  });
}

export async function setTaskLabels(
  taskId: number,
  labelIds: number[]
): Promise<void> {
  await api(`/api/tasks/${taskId}/labels`, {
    method: "PUT",
    body: { label_ids: labelIds },
  });
  // Mirror the new selection into the projectSignal so all open views reflect it.
  const state = projectSignal.value;
  let changed = false;
  const next: Record<number, Task[]> = {};
  for (const [phaseId, list] of Object.entries(state.tasksByPhase)) {
    let listChanged = false;
    const nextList = list.map((t) => {
      if (t.id !== taskId) return t;
      listChanged = true;
      changed = true;
      return { ...t, label_ids: labelIds };
    });
    next[Number(phaseId)] = listChanged ? nextList : list;
  }
  if (changed) {
    projectSignal.value = { ...state, tasksByPhase: next };
  }
}
